
import React, { useState, useEffect, useRef } from 'react';
import { cn } from "@/lib/utils";

interface OptimizedImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  src: string;
  alt: string;
  fill?: boolean;
  sizes?: string;
  priority?: boolean;
  quality?: number;
  fallbackSrc?: string;
  objectFit?: 'cover' | 'contain' | 'fill' | 'none' | 'scale-down';
  containerClassName?: string;
  onLoadingComplete?: () => void;
  aspectRatio?: number;
  className?: string;
}

/**
 * OptimizedImage - A unified image component that optimizes loading performance
 * 
 * Features:
 * - WebP conversion for supported browsers
 * - Lazy loading with priority option
 * - Responsive image handling with srcSet
 * - Fallback image support
 * - Loading indicators and error states
 */
const OptimizedImage = ({
  src,
  alt,
  fill = false,
  sizes = "100vw",
  priority = false,
  quality = 80,
  fallbackSrc = "/placeholder.svg",
  objectFit = 'cover',
  containerClassName,
  onLoadingComplete,
  aspectRatio,
  className,
  ...props
}: OptimizedImageProps) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [loadStartTime] = useState<number>(performance.now());
  const imgRef = useRef<HTMLImageElement>(null);
  
  // Get optimized image URL with WebP if browser supports it
  const optimizedSrc = getOptimizedImageUrl(src, quality);
  
  // Generate srcSet for responsive images
  const srcSet = createSrcSet(src, [400, 800, 1200, 1600], quality);
  
  // Preload high priority images
  useEffect(() => {
    if (priority && optimizedSrc && typeof window !== 'undefined') {
      const preloadLink = document.createElement('link');
      preloadLink.rel = 'preload';
      preloadLink.as = 'image';
      preloadLink.href = optimizedSrc;
      if (sizes) {
        preloadLink.setAttribute('sizes', sizes);
      }
      document.head.appendChild(preloadLink);
      
      return () => {
        try {
          document.head.removeChild(preloadLink);
        } catch (e) {
          // Link might already be removed
        }
      };
    }
  }, [optimizedSrc, priority, sizes]);
  
  // Handle successful image load
  const handleLoad = () => {
    setLoading(false);
    
    // Log performance metrics in development
    if (process.env.NODE_ENV === 'development') {
      const loadTime = performance.now() - loadStartTime;
      console.log(`Image loaded in ${loadTime.toFixed(2)}ms:`, src);
    }
    
    if (onLoadingComplete) {
      onLoadingComplete();
    }
  };
  
  // Handle image load error
  const handleError = () => {
    setError(true);
    setLoading(false);
    console.error(`Failed to load image: ${src}`);
  };

  // Determine container style based on fill and aspectRatio props
  const getContainerStyle = (): React.CSSProperties => {
    if (fill) {
      return { 
        position: 'relative' as const, 
        width: '100%', 
        height: '100%' 
      };
    } else if (aspectRatio) {
      return { 
        position: 'relative' as const, 
        width: '100%', 
        paddingBottom: `${(1 / aspectRatio) * 100}%` 
      };
    }
    return {};
  };

  return (
    <div 
      className={cn(
        "overflow-hidden bg-gray-100",
        containerClassName,
        fill ? "w-full h-full relative" : "",
        aspectRatio ? "relative" : ""
      )}
      style={getContainerStyle()}
    >
      {/* Show loading indicator while image is loading */}
      {loading && !error && (
        <div 
          className="absolute inset-0 bg-gray-200 animate-pulse"
          aria-hidden="true"
        />
      )}
      
      {/* The actual image */}
      <img
        ref={imgRef}
        src={error ? fallbackSrc : optimizedSrc}
        srcSet={!error ? srcSet : undefined}
        alt={alt}
        sizes={sizes}
        onLoad={handleLoad}
        onError={handleError}
        loading={priority ? "eager" : "lazy"}
        className={cn(
          "transition-opacity duration-300",
          loading ? "opacity-0" : "opacity-100",
          fill ? "absolute inset-0 w-full h-full" : "max-w-full h-auto",
          className
        )}
        style={{ 
          objectFit,
          ...(!fill && !aspectRatio ? {} : { 
            position: 'absolute' as const, 
            top: 0, 
            left: 0, 
            width: '100%', 
            height: '100%' 
          })
        }}
        fetchPriority={priority ? "high" : "auto"}
        {...props}
      />
      
      {/* Show fallback on error */}
      {error && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
          <span className="text-gray-500 text-sm">Kuvan lataus epäonnistui</span>
        </div>
      )}
    </div>
  );
};

/**
 * Get optimized image URL with WebP conversion if supported
 */
const getOptimizedImageUrl = (url: string, quality: number = 80): string => {
  if (!url) return '';
  
  // Check if URL is already using optimization parameters
  if (url.includes('?w=') || url.includes('fmt=webp')) return url;
  
  // For Unsplash images, use their optimization parameters
  if (url.includes('unsplash.com')) {
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}auto=format&q=${quality}&w=auto`;
  }
  
  // For images from our own domain, depend on browser WebP support
  const supportsWebP = typeof window !== 'undefined' && 
    document.createElement('canvas').toDataURL('image/webp').indexOf('data:image/webp') === 0;
  
  if (supportsWebP && /\.(jpe?g|png)$/i.test(url)) {
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}fmt=webp&q=${quality}`;
  }
  
  return url;
};

/**
 * Create srcSet for responsive images
 */
const createSrcSet = (url: string, widths: number[], quality: number = 80): string => {
  if (!url) return '';
  
  // For Unsplash images
  if (url.includes('unsplash.com')) {
    return widths
      .map(width => {
        const imgUrl = `${url.includes('?') ? url + '&' : url + '?'}w=${width}&q=${quality}&auto=format`;
        return `${imgUrl} ${width}w`;
      })
      .join(', ');
  }
  
  // For our own images, generate WebP srcset if browser supports it
  const supportsWebP = typeof window !== 'undefined' && 
    document.createElement('canvas').toDataURL('image/webp').indexOf('data:image/webp') === 0;
  
  if (supportsWebP && /\.(jpe?g|png)$/i.test(url)) {
    return widths
      .map(width => {
        const imgUrl = `${url.includes('?') ? url + '&' : url + '?'}w=${width}&q=${quality}&fmt=webp`;
        return `${imgUrl} ${width}w`;
      })
      .join(', ');
  }
  
  return '';
};

export default OptimizedImage;
